import { sessionRequests } from './sessionRequests';

const STORAGE_KEY = 'atrivial:google-signup-email-preference:v1';
const EXPIRY_MS = 30 * 60 * 1000;

type PendingPreference = {
  receivesMarketingEmails: boolean;
  startedAt: number;
  userId?: string;
};

// Embedded sign-in can still keep its choice when browser storage is unavailable.
// A redirect without storage leaves the new account's database default disabled.
let inMemoryPreference: string | null = null;

function readPendingPreference(): string | null {
  try {
    return sessionStorage.getItem(STORAGE_KEY) ?? inMemoryPreference;
  } catch {
    return inMemoryPreference;
  }
}

function writePendingPreference(value: string): void {
  inMemoryPreference = value;
  try {
    sessionStorage.setItem(STORAGE_KEY, value);
  } catch {
    // An email preference must not prevent authentication in restricted browsers.
  }
}

/** Keep the choice in this tab across Google's redirect when storage is available. */
export function beginGoogleSignupEmailPreference(receivesMarketingEmails: boolean): void {
  writePendingPreference(JSON.stringify({ receivesMarketingEmails, startedAt: Date.now() }));
}

export function cancelGoogleSignupEmailPreference(): void {
  inMemoryPreference = null;
  try {
    sessionStorage.removeItem(STORAGE_KEY);
  } catch {
    // Nothing durable was stored when browser storage was unavailable.
  }
}

/** Called after session installation, outside Supabase's synchronous auth callback. */
export async function completeGoogleSignupEmailPreference(userId: string): Promise<void> {
  const raw = readPendingPreference();
  if (!raw) return;
  let preference: PendingPreference;
  try {
    preference = JSON.parse(raw) as PendingPreference;
    if (
      !preference ||
      typeof preference.receivesMarketingEmails !== 'boolean' ||
      typeof preference.startedAt !== 'number' ||
      !Number.isFinite(preference.startedAt) ||
      Date.now() - preference.startedAt > EXPIRY_MS ||
      preference.startedAt > Date.now() ||
      (preference.userId !== undefined && preference.userId !== userId)
    ) {
      cancelGoogleSignupEmailPreference();
      return;
    }
  } catch {
    cancelGoogleSignupEmailPreference();
    return;
  }

  // Bind retries to the first installed identity. The RPC checks the same identity and
  // atomically consumes only a new Google account's pending preference, never a returning user's.
  const bound = JSON.stringify({ ...preference, userId });
  writePendingPreference(bound);
  const assertOwner = sessionRequests.captureOwner();
  const { initializeSignupEmailPreference } = await import('./internal/supabase');
  assertOwner();
  if (readPendingPreference() !== bound) return;
  await initializeSignupEmailPreference(userId, preference.receivesMarketingEmails);
  if (readPendingPreference() === bound) cancelGoogleSignupEmailPreference();
}
